// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  LOAProviderStatusDTO,
  ProviderStatusUpdateRequest,
  ProviderStatusUpdateResult,
  PolicyProviderStatusHistoryDTO
} from '../types/providerStatus';

/**
 * Resolve the ProfileRequest id linked to an LOA via {@code request_id}
 * (V112 FK), exposed on LOADTO as of Phase B (PR 2a). Used by the
 * provider-status endpoints which now route through the unified
 * /api/requests/{requestId}/* surface.
 */
async function resolveRequestId(loaId: string): Promise<string> {
  const loa = await api.get<{ requestId?: string }>(`/loa/${loaId}`);
  const requestId = loa.data.requestId;
  if (!requestId) {
    throw new Error(
      'LOA has no linked Profile Request — Profile Requests must be enabled for this tenant.',
    );
  }
  return requestId;
}

/**
 * Get provider status for all policies on an LOA. Routed through
 * {@code GET /api/requests/{requestId}/provider-status}.
 */
export const getProviderStatus = async (loaId: string): Promise<LOAProviderStatusDTO> => {
  const requestId = await resolveRequestId(loaId);
  const response = await api.get<LOAProviderStatusDTO>(`/requests/${requestId}/provider-status`);
  return response.data;
};

/**
 * Update provider status for a specific policy on an LOA. Routed through
 * {@code PUT /api/requests/{requestId}/policies/{policyId}/provider-status}.
 */
export const updateProviderStatus = async (
  loaId: string,
  policyId: string,
  request: ProviderStatusUpdateRequest
): Promise<ProviderStatusUpdateResult> => {
  const requestId = await resolveRequestId(loaId);
  const response = await api.put<ProviderStatusUpdateResult>(
    `/requests/${requestId}/policies/${policyId}/provider-status`,
    request
  );
  return response.data;
};

/**
 * Get provider status history for a policy across all completed LOAs
 */
export const getProviderStatusHistory = async (
  policyId: string
): Promise<PolicyProviderStatusHistoryDTO> => {
  const response = await api.get<PolicyProviderStatusHistoryDTO>(
    `/policies/${policyId}/provider-status-history`
  );
  return response.data;
};

export default {
  getProviderStatus,
  updateProviderStatus,
  getProviderStatusHistory,
};
